import { useEffect, useState } from 'react'
import { PageContainer, ProTable, type ProColumns } from '@ant-design/pro-components'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { Alert, App, Button, Space, Tag, Typography, type MenuProps, type TableProps } from 'antd'
import { ChevronDown, Layers, ListChecks, Plus } from 'lucide-react'
import { z } from 'zod'
import type { AccountDisplay } from '@super-mega/server/contracts/profile'
import { TableRowActions } from '@/components/table-row-actions.tsx'
import { CREATED_AT_SORT_DIRECTIONS, DEFAULT_PAGE_SEARCH as DEFAULT_QUERY } from '@/const/list.ts'
import { RANGE_PRESETS } from '@/const/range-presets.ts'
import { AuthorizedDropdown } from '@/features/authorization/authorized-dropdown.tsx'
import { Authorized } from '@/features/authorization/authorized.tsx'
import {
  ROOM_STATUS_ENUM,
  ROOM_STATUS_OPTIONS,
  ROOM_TYPE_ENUM,
  RoomBatchModal,
  RoomEditorModal
} from '@/features/master-data/index.ts'
import { UserAvatarName } from '@/features/profile/index.ts'
import {
  useMemberCreatorOptions,
  usePageTableNavigation,
  type DateRangeFormValue,
  type PageTableSearch
} from '@/hooks/index.ts'
import { usePersistedListQuery } from '@/hooks/use-persisted-list-query.ts'
import { useTRPC } from '@/trpc/react.ts'
import { getErrorMessage } from '@/utils/error.ts'
import { createListQueryPersistence } from '@/utils/list-query-persistence.ts'
import {
  createdAtSearchShape,
  createdAtSortSearchShape,
  creatorMemberSearchShape,
  getCreatedAtSearch,
  getCreatedAtSortOrder,
  getDateRangeFormValue
} from '@/utils/list-query.ts'
import { proRemoteSearch } from '@/utils/pro-select-search.ts'

type RoomType = 'office' | 'workstation' | 'warehouse' | 'other'
type RoomStatus = 'vacant' | 'reserved' | 'occupied' | 'maintenance' | 'disabled'

type RoomQuery = PageTableSearch & {
  id?: number
  keyword?: string
  creatorMemberId?: number
  createdFrom?: string
  createdTo?: string
  siteId?: number
  sortField?: 'createdAt'
  sortOrder?: 'asc' | 'desc'
  status?: RoomStatus
  roomType?: RoomType
}

type RoomSearchFormValues = Pick<RoomQuery, 'creatorMemberId' | 'keyword' | 'siteId' | 'status' | 'roomType'> & {
  createdAtRange?: DateRangeFormValue
}

type RoomRow = {
  id: number
  siteId: number
  site: {
    id: number
    name: string
    organization: { id: number; name: string } | null
  }
  roomNo: string
  fullAddress: string
  floor: string | null
  roomType: RoomType
  area: string | null
  status: RoomStatus
  note: string | null
  creator: AccountDisplay
  createdAt: Date
  updatedAt: Date
}

const roomSearchSchema = z.object({
  current: z.coerce.number().int().min(1).catch(DEFAULT_QUERY.current),
  pageSize: z.coerce.number().int().min(1).max(100).catch(DEFAULT_QUERY.pageSize),
  id: z.coerce.number().int().positive().optional().catch(undefined),
  keyword: z.string().trim().max(255).optional().catch(undefined),
  ...creatorMemberSearchShape,
  ...createdAtSearchShape,
  ...createdAtSortSearchShape,
  siteId: z.coerce.number().int().positive().optional().catch(undefined),
  status: z.enum(['vacant', 'reserved', 'occupied', 'maintenance', 'disabled']).optional().catch(undefined),
  roomType: z.enum(['office', 'workstation', 'warehouse', 'other']).optional().catch(undefined)
})

const listQuery = createListQueryPersistence('super-mega:address:rooms:list-query', roomSearchSchema)

export const Route = createFileRoute('/_authenticated/address/rooms')({
  beforeLoad: (options) => {
    listQuery.restore(options)
  },
  component: RoomsPage,
  head: () => ({ meta: [{ title: '房源管理 · Super Mega' }] }),
  validateSearch: listQuery.validateSearch
})

function RoomsPage() {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const { message, modal } = App.useApp()
  const { currentSession } = Route.useRouteContext()
  const navigate = Route.useNavigate()
  const { search: query, navigateSearch } = usePersistedListQuery({
    persistence: listQuery,
    raw: Route.useSearch(),
    accountId: currentSession.id,
    navigate
  })
  const [editing, setEditing] = useState<RoomRow | 'new'>()
  const [batchOpen, setBatchOpen] = useState(false)
  const [selectedRoomIds, setSelectedRoomIds] = useState<number[]>([])
  const [siteKeyword, setSiteKeyword] = useState('')
  const [siteSearchKeyword, setSiteSearchKeyword] = useState<string>()
  const canManage =
    currentSession.isSuperAdmin ||
    currentSession.permissions.some((permission) => permission.startsWith('room.') && !permission.endsWith('.read'))
  const rooms = useQuery({
    ...trpc.room.list.queryOptions(query),
    placeholderData: keepPreviousData
  })
  const memberCreator = useMemberCreatorOptions('room.read', query.creatorMemberId)
  const sites = useQuery({
    ...trpc.lookup.sites.queryOptions({ purpose: 'room.read', keyword: siteSearchKeyword, selectedId: query.siteId }),
    staleTime: 60_000
  })
  const formSites = useQuery({
    ...trpc.lookup.sites.queryOptions({ purpose: 'room.read' }),
    staleTime: 60_000
  })
  const setStatus = useMutation(trpc.room.setStatus.mutationOptions())
  const remove = useMutation(trpc.room.remove.mutationOptions())
  const batchSetStatus = useMutation(trpc.room.batchSetStatus.mutationOptions())
  const batchRemove = useMutation(trpc.room.batchRemove.mutationOptions())

  const closeEditor = () => setEditing(undefined)
  const invalidateList = () => queryClient.invalidateQueries(trpc.room.list.queryFilter())
  const siteOptions = (sites.data ?? []).map((site) => ({ label: site.label, value: site.value }))
  const formSiteOptions = (formSites.data ?? []).map((site) => ({ label: site.label, value: site.value }))
  useEffect(() => {
    const timer = setTimeout(() => setSiteSearchKeyword(siteKeyword.trim() || undefined), 300)
    return () => clearTimeout(timer)
  }, [siteKeyword])
  const table = usePageTableNavigation<RoomQuery, RoomSearchFormValues>({
    defaultSearch: DEFAULT_QUERY,
    getFormInitialValues: (search) => ({
      createdAtRange: getDateRangeFormValue(search.createdFrom, search.createdTo),
      creatorMemberId: search.creatorMemberId,
      keyword: search.keyword ?? (search.id === undefined ? undefined : String(search.id)),
      roomType: search.roomType,
      siteId: search.siteId,
      status: search.status
    }),
    getSearchFromForm: (values) => ({
      ...getCreatedAtSearch(values.createdAtRange),
      creatorMemberId: values.creatorMemberId,
      id: undefined,
      keyword: values.keyword?.trim() || undefined,
      roomType: values.roomType,
      siteId: values.siteId,
      status: values.status
    }),
    navigate: navigateSearch,
    onQueryChange: () => {
      closeEditor()
      setSelectedRoomIds([])
    },
    search: query,
    showTotal: (total) => `共 ${total} 个房间`
  })
  const handleReset = () => {
    setSiteKeyword('')
    setSiteSearchKeyword(undefined)
    table.onReset()
  }

  const handleTableChange: NonNullable<TableProps<RoomRow>['onChange']> =
    /* eslint-disable max-params -- Ant Design Table 的回调签名由组件定义。 */
    (_pagination, _filters, sorter, extra) => {
      if (extra.action !== 'sort') {
        return
      }
      const currentSorter = Array.isArray(sorter) ? sorter[0] : sorter
      const sortOrder = getCreatedAtSortOrder(currentSorter.order)
      closeEditor()
      setSelectedRoomIds([])
      void navigate({
        replace: true,
        search: (previous) => ({
          ...previous,
          current: 1,
          sortField: 'createdAt',
          sortOrder
        })
      })
    }
  /* eslint-enable max-params */

  const changeStatus = async (room: RoomRow, status: RoomStatus) => {
    try {
      await setStatus.mutateAsync({ id: room.id, status })
      void message.success('房间状态已更新')
      await invalidateList()
    } catch (error) {
      void message.error(getErrorMessage(error, '房间状态更新失败'))
    }
  }

  const removeRoom = async (room: RoomRow) => {
    try {
      await remove.mutateAsync({ id: room.id })
      void message.success('房间已删除')
      if (rooms.data?.data.length === 1 && query.current > 1) {
        await navigateSearch({ search: (previous) => ({ ...previous, current: previous.current - 1 }) })
      } else {
        await invalidateList()
      }
    } catch (error) {
      void message.error(getErrorMessage(error, '房间删除失败'))
    }
  }

  const confirmRemoveRoom = (room: RoomRow) => {
    modal.confirm({
      content: '删除后将从房源列表移除，历史数据仍会保留。',
      okButtonProps: { danger: true },
      onOk: () => removeRoom(room),
      title: '确认删除该房间？'
    })
  }

  const changeSelectedStatus = async (status: RoomStatus) => {
    try {
      const result = await batchSetStatus.mutateAsync({ ids: selectedRoomIds, status })
      setSelectedRoomIds([])
      void message.success(`已将 ${result.count} 个房间设为${ROOM_STATUS_ENUM[status].text}`)
      await invalidateList()
    } catch (error) {
      void message.error(getErrorMessage(error, '批量更新房间状态失败'))
    }
  }

  const removeSelectedRooms = async () => {
    const selectedRoomIdSet = new Set(selectedRoomIds)
    const selectedRooms = rooms.data?.data.filter((room) => selectedRoomIdSet.has(room.id)) ?? []
    if (selectedRooms.some((room) => room.status === 'occupied')) {
      void message.warning('选中的房间包含使用中房间，请先释放占用')
      return
    }
    try {
      const result = await batchRemove.mutateAsync({ ids: selectedRoomIds })
      const allVisibleRowsSelected = selectedRooms.length > 0 && selectedRooms.length === (rooms.data?.data.length ?? 0)
      setSelectedRoomIds([])
      void message.success(`已删除 ${result.count} 个房间`)
      if (allVisibleRowsSelected && query.current > 1) {
        await navigateSearch({ search: (previous) => ({ ...previous, current: previous.current - 1 }) })
      } else {
        await invalidateList()
      }
    } catch (error) {
      void message.error(getErrorMessage(error, '批量删除房间失败'))
    }
  }

  const confirmChangeSelectedStatus = (status: RoomStatus) => {
    if (status !== 'disabled') {
      void changeSelectedStatus(status)
      return
    }
    modal.confirm({
      content: '停用后，选中的房间不能再用于订单选房。',
      okButtonProps: { danger: true },
      okText: '确认停用',
      onOk: () => changeSelectedStatus(status),
      title: `停用已选 ${selectedRoomIds.length} 个房间？`
    })
  }

  const confirmRemoveSelectedRooms = () => {
    modal.confirm({
      content: '删除后将从房源列表移除，历史数据仍会保留；使用中的房间无法删除。',
      okButtonProps: { danger: true },
      okText: '确认删除',
      onOk: removeSelectedRooms,
      title: `删除已选 ${selectedRoomIds.length} 个房间？`
    })
  }

  const batchMenu: MenuProps = {
    items: [
      ...ROOM_STATUS_OPTIONS.map((option) => ({ key: option.value, label: `批量设为${option.label}` })),
      { type: 'divider' as const },
      { danger: true, key: 'delete', label: '批量删除' }
    ],
    onClick: ({ key }) => {
      if (key === 'delete') {
        confirmRemoveSelectedRooms()
        return
      }
      confirmChangeSelectedStatus(key as RoomStatus)
    }
  }

  const columns: ProColumns<RoomRow>[] = [
    {
      dataIndex: 'keyword',
      fieldProps: { allowClear: true, placeholder: '房间号、楼层或 ID' },
      hideInTable: true,
      title: '关键词 / ID'
    },
    {
      dataIndex: 'creatorMemberId',
      fieldProps: {
        allowClear: true,
        loading: memberCreator.isLoading,
        options: memberCreator.options,
        placeholder: '选择创建人',
        ...proRemoteSearch(memberCreator.onSearch)
      },
      hideInTable: true,
      title: '创建人',
      valueType: 'select'
    },
    {
      dataIndex: 'createdAtRange',
      hideInTable: true,
      title: '创建时间',
      fieldProps: { presets: RANGE_PRESETS },
      valueType: 'dateRange'
    },
    {
      dataIndex: 'siteId',
      fieldProps: {
        allowClear: true,
        loading: sites.isLoading || sites.isFetching,
        options: siteOptions,
        placeholder: '输入关键词搜索场地',
        ...proRemoteSearch(setSiteKeyword)
      },
      hideInTable: true,
      title: '场地',
      valueType: 'select'
    },
    {
      dataIndex: 'id',
      copyable: true,
      renderText: (value?: number) => (value === undefined ? '—' : String(value)),
      search: false,
      title: 'ID',
      width: '7rem'
    },
    {
      dataIndex: 'roomNo',
      render: (_, room) => (
        <Space orientation="vertical" size={0}>
          <Typography.Text strong>{room.roomNo}</Typography.Text>
          <Typography.Text type="secondary">{room.floor ? `${room.floor}` : '未填楼层'}</Typography.Text>
        </Space>
      ),
      search: false,
      title: '房间号',
      width: '9rem'
    },
    {
      dataIndex: 'status',
      render: (_, room) => {
        const status = ROOM_STATUS_ENUM[room.status]
        return <Tag color={status.color}>{status.text}</Tag>
      },
      title: '状态',
      valueEnum: Object.fromEntries(ROOM_STATUS_OPTIONS.map((option) => [option.value, { text: option.label }])),
      width: '7rem'
    },
    {
      dataIndex: 'site',
      ellipsis: true,
      renderText: (_, room) => room.site.name,
      search: false,
      title: '所属场地',
      width: '12rem'
    },
    {
      dataIndex: 'fullAddress',
      copyable: true,
      ellipsis: true,
      renderText: (_, room) => room.fullAddress,
      search: false,
      title: '完整地址',
      width: '20rem'
    },
    {
      dataIndex: 'organization',
      render: (_, room) =>
        room.site.organization === null ? (
          <Typography.Text type="secondary">—</Typography.Text>
        ) : (
          room.site.organization.name
        ),
      search: false,
      title: '归属主体',
      width: '12rem'
    },
    {
      dataIndex: 'boundEnterprises',
      renderText: () => <Typography.Text type="secondary">—</Typography.Text>,
      search: false,
      title: '绑定企业',
      tooltip: '后续接入入驻/订单数据后展示与房间绑定的企业',
      width: '12rem'
    },
    {
      dataIndex: 'roomType',
      render: (_, room) => ROOM_TYPE_ENUM[room.roomType].text,
      title: '类型',
      valueEnum: ROOM_TYPE_ENUM,
      width: '7rem'
    },
    {
      dataIndex: 'area',
      renderText: (value?: string | null) => (value === null || value === undefined ? '—' : `${value} ㎡`),
      search: false,
      title: '面积',
      width: '7rem'
    },

    {
      dataIndex: 'note',
      ellipsis: true,
      renderText: (value?: string | null) => value || '—',
      search: false,
      title: '备注',
      width: '12rem'
    },
    {
      dataIndex: 'creator',
      render: (_, room) => <UserAvatarName avatar={room.creator.avatar} name={room.creator.name} size={28} />,
      search: false,
      title: '创建人',
      width: '10rem'
    },
    {
      dataIndex: 'createdAt',
      search: false,
      sortOrder: query.sortOrder === 'asc' ? 'ascend' : 'descend',
      sorter: true,
      sortDirections: CREATED_AT_SORT_DIRECTIONS,
      title: '创建时间',
      valueType: 'dateTime',
      width: '12rem'
    },
    {
      fixed: 'right',
      align: 'center',
      render: (_, room) =>
        canManage ? (
          <TableRowActions
            resource="room"
            actions={[
              ...(currentSession.isSuperAdmin ||
              currentSession.permissions.some(
                (permission) => permission === 'order.rental.read' || permission === 'order.service.read'
              )
                ? [
                    {
                      key: 'history',
                      label: '租赁记录',
                      render: (
                        <Link
                          to="/address/rooms/$roomId/rentals"
                          params={{ roomId: String(room.id) }}
                          search={{ current: 1, pageSize: 20 }}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          租赁记录
                        </Link>
                      )
                    }
                  ]
                : []),
              { key: 'edit', label: '编辑', onClick: () => setEditing(room) },
              ...ROOM_STATUS_OPTIONS.map((option) => ({
                key: `status-${option.value}`,
                label: `设为${option.label}`,
                onClick: () => changeStatus(room, option.value as RoomStatus)
              })),
              { danger: true, key: 'delete', label: '删除', onClick: () => confirmRemoveRoom(room) }
            ]}
          />
        ) : currentSession.isSuperAdmin ||
          currentSession.permissions.some(
            (permission) => permission === 'order.rental.read' || permission === 'order.service.read'
          ) ? (
          <Link
            to="/address/rooms/$roomId/rentals"
            params={{ roomId: String(room.id) }}
            search={{ current: 1, pageSize: 20 }}
            target="_blank"
            rel="noopener noreferrer"
          >
            租赁记录
          </Link>
        ) : (
          '—'
        ),
      search: false,
      title: '操作',
      valueType: 'option',
      width: '10rem'
    }
  ]

  return (
    <PageContainer
      breadcrumbRender={false}
      content="管理场地内划分的房间；支持编号区间或粘贴列表批量生成，房间状态可用于订单选房。"
      title="房源管理"
    >
      {rooms.isError ? (
        <Alert
          action={
            <Button onClick={() => void rooms.refetch()} size="small">
              重试
            </Button>
          }
          className="mb-4"
          showIcon
          title={getErrorMessage(rooms.error, '房源列表加载失败')}
          type="error"
        />
      ) : null}
      <ProTable<RoomRow, RoomSearchFormValues>
        columns={columns}
        columnsState={{
          defaultValue: { boundEnterprises: { show: false } },
          persistenceKey: 'master-data-rooms:table',
          persistenceType: 'localStorage'
        }}
        dataSource={rooms.data?.data ?? []}
        loading={{ delay: 150, spinning: rooms.isFetching }}
        onChange={handleTableChange}
        onReset={handleReset}
        onSubmit={table.onSubmit}
        options={{ reload: () => void rooms.refetch() }}
        pagination={{ ...table.pagination, total: rooms.data?.total ?? 0 }}
        formRef={table.formRef}
        rowKey="id"
        rowSelection={
          canManage
            ? {
                onChange: (keys) => setSelectedRoomIds(keys.map(Number)),
                selectedRowKeys: selectedRoomIds
              }
            : undefined
        }
        scroll={{ x: '126rem' }}
        form={{ initialValues: table.formInitialValues }}
        search={{
          defaultCollapsed: false,
          labelWidth: 'auto',
          span: { xs: 24, sm: 12, md: 8, lg: 6, xl: 6, xxl: 6 }
        }}
        size="small"
        toolBarRender={() =>
          canManage
            ? [
                <AuthorizedDropdown
                  resource="room"
                  key="batch-actions"
                  menu={batchMenu}
                  placement="bottomRight"
                  trigger={['click']}
                >
                  <Button disabled={selectedRoomIds.length === 0} icon={<ListChecks aria-hidden size={16} />}>
                    批量操作 <ChevronDown aria-hidden size={14} />
                  </Button>
                </AuthorizedDropdown>,
                <Authorized key="new" permissions={['room.create']}>
                  <Button
                    icon={<Plus aria-hidden size={16} />}
                    key="new"
                    onClick={() => setEditing('new')}
                    type="primary"
                  >
                    新建房间
                  </Button>
                </Authorized>,
                <Button icon={<Layers aria-hidden size={16} />} key="batch" onClick={() => setBatchOpen(true)}>
                  批量生成
                </Button>
              ]
            : []
        }
      />

      <RoomEditorModal
        editing={editing}
        onClose={closeEditor}
        onSaved={async () => {
          closeEditor()
          await invalidateList()
        }}
        siteOptions={formSiteOptions}
      />
      <RoomBatchModal
        onClose={() => setBatchOpen(false)}
        onSaved={async () => {
          setBatchOpen(false)
          await invalidateList()
        }}
        open={batchOpen}
        siteOptions={formSiteOptions}
      />
    </PageContainer>
  )
}
